// added for WISPR-lab/data-export-gui
import { getDB } from '../index.js';
import { hexColor } from '@/utils/hex.js';

// Coerce epoch-zero timestamps to null so they never surface in the UI.
function nullTs(val) {
  if (val === null || val === undefined) return null;
  var s = String(val).trim();
  if (s === '0' || s === '0.0' || s === '' || s.indexOf('1970-01-01') === 0) return null;
  var n = Number(val);
  if (!isNaN(n) && n === 0) return null;
  return val;
}

var EPOCH_ZERO_KEYS = ['entity_first_seen_timestamp', 'entity_last_seen_timestamp', 'timestamp'];

function parseTags(raw) {
  if (Array.isArray(raw)) return raw;
  if (!raw) return [];
  try {
    var parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

export async function getResolvedSessionsRegistrations(dbName) {
  const db = await getDB(dbName);

  // Subquery pulls the device_group_id for sessions via their client_session_id →
  // events → device_group_events join. LIMIT 1 handles the case where multiple
  // events for the same session map to the same group.
  const sql = `
    SELECT rsr.*,
      (SELECT die.device_group_id
       FROM events e
       JOIN device_group_events die ON e.id = die.event_id
       WHERE e.upload_id = rsr.upload_id
         AND json_extract(rsr.attributes, '$.client_session_id') IS NOT NULL
         AND json_extract(e.attributes, '$.client_session_id')
             = json_extract(rsr.attributes, '$.client_session_id')
       LIMIT 1) AS group_id,
      u.color  AS upload_color,
      u.platform AS upload_platform
    FROM resolved_sessions_registrations rsr
    LEFT JOIN uploads u ON rsr.upload_id = u.id
  `;

  const rows = await db.exec(sql, {
    returnValue: 'resultRows',
    rowMode: 'object'
  });

  // Batch resolve raw_data ids -> filename + line numbers (same as events.js)
  const rowRawDataIds = rows.map(row => {
    try {
      return row.raw_data_ids ? JSON.parse(row.raw_data_ids).filter(Boolean) : [];
    } catch (e) {
      return [];
    }
  });

  const rawDataMap = {};
  const allRawDataIds = [...new Set(rowRawDataIds.flat())];
  if (allRawDataIds.length > 0) {
    const placeholders = allRawDataIds.map(() => '?').join(',');
    const rawRows = await db.exec(
      `SELECT rd.id, rd.line_numbers, uf.opfs_filename
       FROM raw_data rd
       LEFT JOIN uploaded_files uf ON uf.id = rd.file_id
       WHERE rd.id IN (${placeholders})`,
      { bind: allRawDataIds, returnValue: 'resultRows', rowMode: 'object' }
    );
    rawRows.forEach(rd => {
      let lines = [];
      try {
        lines = rd.line_numbers ? JSON.parse(rd.line_numbers) : [];
      } catch (e) {}
      rawDataMap[rd.id] = { filename: rd.opfs_filename || 'Unknown File', line_numbers: lines };
    });
  }

  const resolved = [];

  for (const [rowIndex, row] of rows.entries()) {
    const sourcesInfo = rowRawDataIds[rowIndex].map(rid => rawDataMap[rid]).filter(Boolean);
    const filenames = [...new Set(sourcesInfo.map(s => s.filename))];
    const flatLineNumbers = [...new Set(sourcesInfo.flatMap(s => s.line_numbers))];

    let attrs = {};
    if (row.attributes) {
      try {
        attrs = typeof row.attributes === 'string' ? JSON.parse(row.attributes) : row.attributes;
      } catch (e) {
        attrs = {};
      }
    }

    // Nullify epoch-zero on known timestamp keys in the attrs blob
    EPOCH_ZERO_KEYS.forEach(function(k) {
      if (k in attrs) attrs[k] = nullTs(attrs[k]);
    });

    const cookieVal = attrs['client_session_id'];
    const serialVal = attrs['device_serial_number'];

    let eventsQuery = '';
    let eventCount = 0;

    if (cookieVal) {
      eventsQuery = `client_session_id:"${cookieVal}"`;
      const pattern = String(cookieVal).replace(/\*/g, '%');
      const countSql = `
        SELECT COUNT(*) as count
        FROM events
        WHERE upload_id = ?
          AND json_extract(attributes, '$.client_session_id') LIKE ?
      `;
      const countRes = await db.exec(countSql, {
        bind: [row.upload_id, pattern],
        returnValue: 'resultRows',
        rowMode: 'object'
      });
      eventCount = (countRes && countRes[0]) ? countRes[0].count : 0;

    } else if (serialVal) {
      eventsQuery = `device_serial_number:"${serialVal}"`;
      const pattern = String(serialVal).replace(/\*/g, '%');
      const countSql = `
        SELECT COUNT(*) as count
        FROM events
        WHERE upload_id = ?
          AND json_extract(attributes, '$.device_serial_number') LIKE ?
      `;
      const countRes = await db.exec(countSql, {
        bind: [row.upload_id, pattern],
        returnValue: 'resultRows',
        rowMode: 'object'
      });
      eventCount = (countRes && countRes[0]) ? countRes[0].count : 0;
    }

    resolved.push({
      id: row.id,
      upload_id: row.upload_id,
      entity_type: row.entity_type,
      entity_sub_type: row.entity_sub_type,
      origin: row.origin,
      model_name: row.model_name || (attrs['entity_display_name'] ? `"${attrs['entity_display_name']}"` : 'Unknown Device'),
      client_name: row.client_name,
      os_name: row.os_name,
      os_version: row.os_version,
      os_type: row.os_type,
      group_id: row.group_id || null,
      is_reduced_ua: !!row.is_reduced_ua,
      user_agent_original: attrs['user_agent_original'] || null,
      has_trusted_cookie: !!row.has_trusted_cookie,
      trusted_cookie_id: row.trusted_cookie_id,
      has_passkey: !!row.has_passkey,
      registration_device: row.registration_device,
      upload_color: hexColor(row.upload_color),
      platform: row.upload_platform,
      attributes: attrs,
      events_query: eventsQuery,
      event_count: eventCount,
      tags: parseTags(row.tags),
      filename: filenames[0] || '',
      filenames,
      line_numbers: flatLineNumbers,
      sources: sourcesInfo
    });
  }

  return resolved;
}
